import { truncate } from 'lodash'
import React from 'react'
import { Sankey } from 'recharts'

import {
  getSentimentBarColor,
  getSentimentColorGroup,
} from 'features/charts-v2/constants'
import type {
  SankeyGraph,
  SankeyLinkDatum,
  SankeyNodeDatum,
} from 'features/charts-v2/services/data-transformation-service'
import type { FlowColorMode } from 'features/charts-v2/types/filter-hash/v2'

const NODE_WIDTH_PX = 12
const NODE_PADDING_PX = 8
const LABEL_GAP_PX = 6
const COLUMN_GAP_LABEL_PADDING_PX = 12
const AVERAGE_CHAR_WIDTH_EM = 0.6
const LINK_OPACITY = 0.45
const NEUTRAL_FLOW_COLOR = '#bfbfbf'
const LABEL_COLOR = '#333'
const COUNT_COLOR = '#888'
const FONT_FAMILY =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'

/** Height of the column-name row above the nodes, as a multiple of the font. */
const HEADER_FONT_MULTIPLE = 1.8

type SankeyProps = React.ComponentProps<typeof Sankey>
type SankeyMargin = Record<'top' | 'right' | 'bottom' | 'left', number>
type SankeyNodeRenderer = SankeyProps['node']
type SankeyLinkRenderer = SankeyProps['link']

export interface SankeyLabelLayout {
  fontSizePx: number
  showCounts: boolean
}

export interface SankeyColors {
  /** First-column node id -> colour, which every flow and node inherits from. */
  originColors: Map<string, string>
  flowColorMode: FlowColorMode
}

export interface SankeyHandlers {
  onLinkHover: (linkIndex: number, clientX: number, clientY: number) => void
  onLinkLeave: () => void
  onLinkClick: (linkIndex: number, event: React.MouseEvent) => void
  onNodeHover: (nodeId: string | null) => void
  onNodeClick: (nodeId: string, event: React.MouseEvent) => void
}

interface SankeyDiagramProps {
  graph: SankeyGraph
  columnLabels: string[]
  /** Outer padding around the nodes; the column headings sit in its top. */
  commonMargin: SankeyMargin
  colors: SankeyColors
  labels: SankeyLabelLayout
  handlers: SankeyHandlers
  /** Injected by `ResponsiveContainer`. */
  width?: number
  height?: number
}

const scoreColor = (avg: number | null): string =>
  avg === null ? NEUTRAL_FLOW_COLOR : getSentimentBarColor(avg)

/** Recharts addresses a link's ends by node position, not id. Nodes keep the
 *  graph's order and links keep theirs, so a Recharts node or link `index` is
 *  also the index into `graph.nodes` / `graph.links`. */
const toRechartsFlow = (graph: SankeyGraph): SankeyProps['data'] => {
  const positionById = new Map(
    graph.nodes.map((node, index) => [node.id, index]),
  )
  return {
    nodes: graph.nodes,
    links: graph.links.map((link) => ({
      source: positionById.get(link.source)!,
      target: positionById.get(link.target)!,
      value: link.count,
    })),
  }
}

const SankeyDiagramComponent: React.FC<SankeyDiagramProps> = ({
  graph,
  columnLabels,
  commonMargin,
  colors,
  labels,
  handlers,
  width = 0,
  height = 0,
}) => {
  const { originColors, flowColorMode } = colors
  const headerPx = labels.fontSizePx * HEADER_FONT_MULTIPLE
  // Recharts re-lays-out whenever this reference changes (and, because of a
  // quirk in its prop diffing, on every render), so keep it stable.
  const flow = React.useMemo(() => toRechartsFlow(graph), [graph])
  const margin = React.useMemo(
    () => ({
      top: commonMargin.top + headerPx,
      right: commonMargin.right,
      bottom: commonMargin.bottom,
      left: commonMargin.left,
    }),
    [commonMargin, headerPx],
  )
  // Recharts lays the nodes out inside the margins.
  const contentWidth = width - margin.left - margin.right
  const contentHeight = height - margin.top - margin.bottom

  if (contentWidth <= 0 || contentHeight <= 0) return null

  const columnCount = columnLabels.length
  const columnSpanPx = Math.max(1, columnCount - 1)
  const columnGapPx =
    (contentWidth - NODE_WIDTH_PX) / columnSpanPx - COLUMN_GAP_LABEL_PADDING_PX
  const maxLabelChars = Math.max(
    4,
    Math.floor(columnGapPx / (labels.fontSizePx * AVERAGE_CHAR_WIDTH_EM)),
  )

  const nodeColor = (node: SankeyNodeDatum): string =>
    flowColorMode === 'sentiment'
      ? scoreColor(node.avg)
      : (originColors.get(node.originId) ?? NEUTRAL_FLOW_COLOR)

  const linkColor = (link: SankeyLinkDatum): string =>
    flowColorMode === 'sentiment'
      ? scoreColor(link.avg)
      : (originColors.get(link.originId) ?? NEUTRAL_FLOW_COLOR)

  const renderLink: SankeyLinkRenderer = (props) => {
    const link = graph.links[props.index]
    if (!link) return <g />
    const {
      sourceX,
      sourceY,
      sourceControlX,
      targetControlX,
      targetX,
      targetY,
      linkWidth,
    } = props
    return (
      <path
        d={`M${sourceX},${sourceY}C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}`}
        fill="none"
        stroke={linkColor(link)}
        strokeOpacity={LINK_OPACITY}
        strokeWidth={Math.max(1, linkWidth)}
        cursor="pointer"
        data-sankey-source={link.source}
        data-sankey-target={link.target}
        data-metric-bucket={
          flowColorMode === 'sentiment' && link.avg !== null
            ? getSentimentColorGroup(link.avg)
            : undefined
        }
        onMouseMove={(event) =>
          handlers.onLinkHover(props.index, event.clientX, event.clientY)
        }
        onMouseLeave={handlers.onLinkLeave}
        onClick={(event) => handlers.onLinkClick(props.index, event)}
      />
    )
  }

  const renderNode: SankeyNodeRenderer = (props) => {
    const node = graph.nodes[props.index]
    if (!node) return <g />
    const { x, y, height: nodeHeight } = props
    const isLastColumn = node.column === columnCount - 1
    const showLabel = nodeHeight >= labels.fontSizePx * 0.8
    return (
      <g>
        <rect
          x={x}
          y={y}
          width={NODE_WIDTH_PX}
          height={Math.max(1, nodeHeight)}
          fill={nodeColor(node)}
          cursor="pointer"
          data-sankey-node={node.id}
          onMouseEnter={() => handlers.onNodeHover(node.id)}
          onMouseLeave={() => handlers.onNodeHover(null)}
          onClick={(event) => handlers.onNodeClick(node.id, event)}
        />
        {showLabel && (
          <text
            x={
              isLastColumn ? x - LABEL_GAP_PX : x + NODE_WIDTH_PX + LABEL_GAP_PX
            }
            y={y + nodeHeight / 2}
            dominantBaseline="middle"
            textAnchor={isLastColumn ? 'end' : 'start'}
            fill={LABEL_COLOR}
            fontSize={labels.fontSizePx}
            fontFamily={FONT_FAMILY}
            stroke="#fff"
            strokeWidth={3}
            paintOrder="stroke"
            pointerEvents="none"
          >
            {truncate(node.label, { length: maxLabelChars })}
            {labels.showCounts && (
              <tspan dx={4} fill={COUNT_COLOR}>
                {node.total.toLocaleString()}
              </tspan>
            )}
          </text>
        )}
      </g>
    )
  }

  return (
    <Sankey
      width={width}
      height={height}
      data={flow}
      nameKey="label"
      dataKey="value"
      nodeWidth={NODE_WIDTH_PX}
      nodePadding={NODE_PADDING_PX}
      // No relaxation and no re-sorting: the default 32 iterations reorder
      // nodes to untangle ribbons, which would drop the volume-descending
      // order the graph is built in.
      iterations={0}
      sort={false}
      margin={margin}
      node={renderNode}
      link={renderLink}
    >
      <g className="sankey-column-headings">
        {columnLabels.map((label, column) => {
          const isLast = column === columnCount - 1
          const columnX =
            margin.left +
            (column * (contentWidth - NODE_WIDTH_PX)) / columnSpanPx
          return (
            <text
              key={`header-${column}`}
              x={isLast ? columnX + NODE_WIDTH_PX : columnX}
              y={commonMargin.top + labels.fontSizePx}
              fill={COUNT_COLOR}
              fontSize={labels.fontSizePx}
              fontFamily={FONT_FAMILY}
              fontWeight={600}
              textAnchor={isLast ? 'end' : 'start'}
              pointerEvents="none"
            >
              {truncate(label, { length: maxLabelChars * 2 })}
            </text>
          )
        })}
      </g>
    </Sankey>
  )
}

export const SankeyDiagram = React.memo(SankeyDiagramComponent)
